import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

/**
 * Wrapper for providing some boilerplate A11Y HTML attributes.
 * @param {*} index
 * @returns {Object}
 * @see https://www.a11yproject.com/
 */
const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

/**
 * Simple tabber component wrapper around MUI Tabs.
 * @param {Object} props - The props for the component
 * @param {Array<Object>} props.tabs - Array of tab objects
 * @param {number} props.index - Index for referencing the tab
 * @param {Function} props.onChange - Function for handling tab change
 * @component
 * @example
 * const index = 0
 * const tabs = [{index: 0, label: 'Test 1'}, {index: 1, label: 'Test 2'}]
 * const handleChange = (value) => { console.log(value) }
 * return (
 *   <Tabber index={index} tabs={tabs} onChange={handleChange} />
 * )
 * @see https://mui.com/material-ui/react-tabs/
 */
const Tabber = ({ tabs, index = 0, onChange }) => {
  // Value is the index of the current tab
  const [value, setValue] = useState(0);

  // Parent components can control index this way
  useEffect(() => {
    setValue(index);
  }, [index]);

  // Set the value and call the parent's onChange function
  const handleChange = (_event, newValue) => {
    setValue(newValue);
    onChange(newValue);
  };

  return (
    // 🌎 Main container for the tabber
    <Tabs
      value={value}
      onChange={handleChange}
      aria-label="tab selector"
      textColor="secondary"
      indicatorColor="secondary"
    >

      {/* ➰ For each tab in tabs create a Tab and sort by index */}
      {tabs
        .sort((a, b) => a.index - b.index)
        .map((tab) => (
          <Tab
            key={tab.index}
            label={tab.label}
            sx={{ fontWeight: 'bold' }}
            className="MuiTab"
            {...a11yProps(tab.index)}
          />
        ))
      }

    </Tabs>
  );
};

Tabber.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number,
    label: PropTypes.string,
  })),
  index: PropTypes.number,
  onChange: PropTypes.func,
};

export default Tabber;
